import {
  assistDomainAtom,
  assistLicensesAtom,
  auditLicensesAtom,
  availableAuditEntriesAtom,
  currentAuditEntryIdAtom,
  demoAuditEntry,
  demoModeAtom,
  issueContentLicensesAtom,
  translateEasyLicenses,
  userAtom,
} from '@/global-store';
import { isExpired } from '@helpers/isExpired';
import { api, demoEntry, demoUser } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import type {
  AuditData,
  Responsibility,
  TranslateData,
  WcagCheckerData,
  WebsiteData,
} from '@eye-able/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { offlineLicenses } from './offlineLicenses';

const offlineMode = import.meta.env.VITE_OFFLINE_MODE === 'true';

/** Which issue responsibilities (code / content / design) the org may see. */
export interface IssuesContentLicense {
  id: number;
  name: Responsibility;
}

/** Product modules the org may access, e.g. `plainLanguage`. */
export interface ModuleAccessLicense {
  id: number;
  name: string;
}

/**
 * `/listAllLicenses` returns one array per license type, always in this order.
 * `moduleAccesses` is not consumed here; the products read it via their own
 * queries.
 */
export type LicensesResponse = [
  assist: WebsiteData[],
  audit: WcagCheckerData[],
  report: AuditData[],
  translate: TranslateData[],
  issuesContent: IssuesContentLicense[],
  moduleAccesses: ModuleAccessLicense[],
];

/**
 * What demo mode resolves to instead of fetching: no license of any kind, which
 * puts every product into its demo state.
 */
const noLicenses: LicensesResponse = [[], [], [], [], [], []];

export function useFetchLicenses() {
  const [user, setUser] = useAtom(userAtom);
  const demoMode = useAtomValue(demoModeAtom);
  const [currentAuditEntryId, setCurrentAuditEntryId] = useAtom(
    currentAuditEntryIdAtom,
  );
  const [selectedAssistDomain, setSelectedAssistDomain] =
    useAtom(assistDomainAtom);

  const setAvailableAuditEntries = useSetAtom(availableAuditEntriesAtom);
  const setAuditLicenses = useSetAtom(auditLicensesAtom);
  const setAssistLicenses = useSetAtom(assistLicensesAtom);
  const setIssueContentLicenses = useSetAtom(issueContentLicensesAtom);
  const setTranslateEasyLicenses = useSetAtom(translateEasyLicenses);

  const {
    data: licenses,
    isLoading,
    isError,
    error,
  } = useQuery({
    enabled: !!user,
    queryKey: [api.listAllLicenses, user?.id, user?.isAdmin, demoMode],
    queryFn: () => {
      if (demoMode) return Promise.resolve(noLicenses);

      if (offlineMode) return Promise.resolve(offlineLicenses);

      return fetchWrapper.get<LicensesResponse>(api.listAllLicenses);
    },
  });

  useEffect(() => {
    if (demoMode && !user) {
      setUser(demoUser);
      return;
    }

    if (!user || isError || isLoading || !licenses) return;

    const [
      assistLicenses,
      auditLicenses,
      reportLicenses,
      translateLicenses,
      issuesContentLicenses,
    ] = licenses;

    setIssueContentLicenses(
      issuesContentLicenses.map((license) => license.name),
    );

    if (!reportLicenses.length) {
      setAvailableAuditEntries([demoAuditEntry]);
      setCurrentAuditEntryId(demoEntry.id);
    } else {
      setAvailableAuditEntries(reportLicenses);

      const hasSelectedEntry = reportLicenses.some(
        (entry) => entry.id === currentAuditEntryId,
      );

      if (!hasSelectedEntry) setCurrentAuditEntryId(reportLicenses[0].id);
    }

    const activeAssistLicenses = assistLicenses.filter(
      (license) => license.endDate !== null && !isExpired(license.endDate),
    );

    if (!activeAssistLicenses.length) {
      setAssistLicenses([]);
      setSelectedAssistDomain('testdomain.com');
    } else {
      setAssistLicenses(assistLicenses);

      const hasSelectedDomain = activeAssistLicenses.some(
        (entry) => entry.domain === selectedAssistDomain,
      );

      if (!hasSelectedDomain)
        setSelectedAssistDomain(activeAssistLicenses[0].domain);
    }

    setAuditLicenses(auditLicenses);
    setTranslateEasyLicenses(translateLicenses);
  }, [
    licenses,
    demoMode,
    isLoading,
    isError,
    user,
    setAssistLicenses,
    setAuditLicenses,
    setUser,
    selectedAssistDomain,
    setSelectedAssistDomain,
  ]);

  // The query is disabled until a user exists, so callers must treat a missing
  // user as "not ready" themselves (BaseRoute does, via isUserReady).
  return { isLoading, isError, error };
}

export function useReloadLicenseData() {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [api.listAllLicenses],
    });
}
